/*
  The report as one csv. Excel with Belgian settings is the reader that matters:
  it splits on semicolons and reads a comma as the decimal separator, so that is
  what comes out of here.
*/

import { csvNumber, formatDay, formatPeriod } from './period';

const DELIMITER = ";";

const escape = (value) => {
  const text = String(value ?? "");

  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const line = (cells) => cells.map(escape).join(DELIMITER);

const block = (title, header, rows) => [
  line([title]),
  line(header),
  ...rows.map(line),
  "",
];

export const statisticsFilename = (report, extension) =>
  `tapwallet-statistieken-${report.wallet.code}-${report.period.start}-${report.period.end}.${extension}`;

export const statisticsToCsv = (report) => {
  const rows = [
    line(["TapWallet statistieken"]),
    line(["Wallet", `${report.wallet.name} (${report.wallet.code})`]),
    line(["Bedrijf", report.wallet.company ?? ""]),
    line(["Periode", formatPeriod(report.period)]),
    line(["Aantal dagen", report.period.days]),
    line(["Gegenereerd op", new Date(report.period.generatedAt).toLocaleString("nl-BE")]),
    "",

    ...block("Kasstroom", ["Omschrijving", "Bedrag"], [
      ["Opgeladen", csvNumber(report.cashflow.toppedUp)],
      ["Verbruikt", csvNumber(report.cashflow.spent)],
      ["Netto kasstroom", csvNumber(report.cashflow.net)],
      ["Inkoop", csvNumber(report.cashflow.purchases)],
      ["Resultaat", csvNumber(report.cashflow.result)],
      ["Gratis verbruik (goudkaarten)", csvNumber(report.cashflow.goldSpent)],
      ["Openstaand saldo op de kaarten", csvNumber(report.balance.outstanding)],
    ]),

    ...block("Kerncijfers", ["Omschrijving", "Waarde"], [
      ["Transacties", report.cashflow.transactions],
      ["Actieve kaarten", report.cashflow.activeCards],
      ["Kaarten in omloop", report.balance.cards],
      ["Goudkaarten", report.balance.goldCards],
      ["Verkochte stuks", report.cashflow.unitsSold],
      ["Gemiddelde transactie", csvNumber(report.cashflow.averageTransaction)],
      ["Gemiddeld verbruik per dag", csvNumber(report.cashflow.averagePerDay)],
    ]),

    ...block("Per dag", ["Datum", "Opgeladen", "Verbruikt", "Transacties"],
      report.daily.map((row) => [
        formatDay(row.day),
        csvNumber(row.toppedUp),
        csvNumber(row.spent),
        row.transactions,
      ])),

    ...block("Topverbruikers", ["Plaats", "Naam", "Kaartnr", "Verbruikt", "Opgeladen", "Beurten", "Stuks", "Favoriet"],
      report.leaderboard.map((row) => [
        row.rank,
        row.name,
        row.cardnr,
        csvNumber(row.spent),
        csvNumber(row.toppedUp),
        row.visits,
        row.units,
        row.favourite ? `${row.favourite.name} (${row.favourite.quantity})` : "",
      ])),

    ...block("Producten", ["Product", "Categorie", "Aantal", "Omzet"],
      report.products.map((row) => [
        row.name,
        row.category,
        row.quantity,
        csvNumber(row.revenue),
      ])),

    ...block("Categorieën", ["Categorie", "Aantal", "Omzet"],
      report.categories.map((row) => [
        row.category,
        row.quantity,
        csvNumber(row.revenue),
      ])),

    ...block("Inkoop", ["Leverancier", "Bonnen", "Bedrag"],
      report.purchases.map((row) => [
        row.supplier,
        row.receipts,
        csvNumber(row.total),
      ])),
  ];

  return rows.join("\r\n");
};

export const downloadStatisticsCsv = (report) => {
  // The byte order mark is what makes Excel read the file as utf-8 instead of
  // mangling every accented name in it.
  const blob = new Blob(["﻿", statisticsToCsv(report)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = statisticsFilename(report, "csv");
  link.click();
  URL.revokeObjectURL(url);
};
